import { RealtimeLayer } from "mobility-toolbox-js/ol";
import { unByKey } from "ol/Observable";

import getVehicleImage from "./TralisStyle";

/**
 * Responsible for loading tracker data from Trajserv.
 * @class
 * @param {Object} [options]
 * @inheritDoc
 */
class TralisLayer extends RealtimeLayer {
  constructor(options = {}) {
    super({
      className: "ol-layer tracker-layer-container",
      defaultUserInteractions: false,
      useDebounce: false,
      useRequestAnimationFrame: true,
      useThrottle: false,
      ...options,
      allowRenderWhenAnimating: true,
      debug: false, // Don't activate debug mode for now because on topographic mode it removes the rotation
      generalizationLevelByZoom: [],
      getRefreshTimeInMs() {
        return 30;
      },
      minZoomInterpolation: 1,
      motsByZoom: [],
      sort: (a, b) => {
        if (a.properties.isStanding) {
          return -1;
        }
        if (b.properties.isStanding) {
          return 1;
        }

        return options?.sort?.(a, b) || 0;
      },
      styleOptions: {
        getScreenPixel: (pixel) => pixel,
      },
    });
    this.engine.style = this.defaultStyle.bind(this);
    this.cache = {};
    this.config = options.config;
    this.debugCache = {};

    // debugStyle is use in debugVehicleStyleFunction
    this.debugStyle = options.debug;
  }

  attachToMap() {
    super.attachToMap();
    this.onChangeMode();
  }

  defaultStyle(trajectory, viewState, options) {
    const { raw_time: rawTime, train_id: id } = trajectory.properties || {};
    const {
      debugVehicleStyleFunc,
      selectedVehicleStyleFunc,
      standingVehicleStyleFunc,
    } = this.config || {};
    const isSelected = this.selectedVehicleId === id;

    // [SBAHNMW-884], we hide trains outside the time intervals
    const intervals = trajectory?.properties?.time_intervals;
    const lastTimeInterval = intervals?.[intervals.length - 1]?.[0];
    if (!lastTimeInterval || viewState.time > lastTimeInterval) {
      return null;
    }

    let vehicleCanvas = getVehicleImage(trajectory.properties, this);

    // If the train is selected we apply a custom function if the config provides one.
    if (isSelected && selectedVehicleStyleFunc) {
      vehicleCanvas = selectedVehicleStyleFunc(
        trajectory.properties,
        this,
        vehicleCanvas,
      );
    }

    let canvas = vehicleCanvas;

    // If the train is stopped since 5 min we apply a custom function if the config provides one.
    const isStanding = rawTime && Date.now() - rawTime > 5 * 60 * 1000; // > 5min

    this.trajectories[id].properties.isStanding = isStanding;

    // In debug mode we display more informations about the trains.
    if (this.debugStyle && debugVehicleStyleFunc) {
      canvas = debugVehicleStyleFunc(trajectory.properties, this, canvas);
    }

    // We apply the standing style after the debug style to be sure the pulse effect is well applied
    // and not cached by the debug function.
    if (this.debugStyle && isStanding && standingVehicleStyleFunc) {
      canvas = standingVehicleStyleFunc(
        trajectory.properties,
        this,
        vehicleCanvas,
        canvas,
      );
    }

    return canvas;
  }

  detachFromMap(map) {
    unByKey(this.onChangeResKey);
    super.detachFromMap(map);
  }

  // Display every bus we found

  mustNotBeDisplayed() {
    return false;
  }

  onChangeMode() {
    // When changing mode we re-adapt the icon scale depending on the config.
    // The scale values can be mode dependent if needed.
    const { dfltIconHighlightScale, dfltIconScale, getIconScaleFromRes } =
      this.config;

    const {
      dfltIconHighlightScale: dfltIconHighlightScaleByMode,
      dfltIconScale: dfltIconScaleByMode,
      getIconScaleFromRes: getIconScaleFromResByMode,
    } = this.config[this.mode];

    const scale = dfltIconScaleByMode || dfltIconScale;

    // Define icon scale for highlight;
    const highlightScale =
      dfltIconHighlightScaleByMode || dfltIconHighlightScale || 0.8;
    const getIconScale = getIconScaleFromResByMode || getIconScaleFromRes;

    if (scale) {
      this.dfltIconScale = scale;
      this.minIconScale = this.dfltIconScale * 0.75;
    }

    if (highlightScale) {
      this.dfltIconHighlightScale = highlightScale;
    }

    // Define icon scale
    unByKey(this.onChangeResKey);
    if (getIconScale) {
      this.iconScale = getIconScale(this.map.getView(), this);
      this.onChangeResKey = this.map
        .getView()
        .on("change:resolution", ({ target: view }) => {
          this.iconScale = getIconScale(view, this);
        });
    } else {
      this.iconScale = dfltIconScaleByMode || dfltIconScale || 0.6;
    }
  }

  setMode(mode) {
    super.setMode(mode);
    this.onChangeMode();
  }
}

export default TralisLayer;
